const env = process.env || {}
const envEnabled = String(env.REACT_APP_PASSWORD_ENCRYPTION_ENABLED || 'true').toLowerCase()
const envPublicKey = (env.REACT_APP_PASSWORD_PUBLIC_KEY_PEM || '').replace(/\\n/g, '\n')

const PASSWORD_ENCRYPTION_CONFIG = {
  enabled: envEnabled === 'true',
  algorithm: env.REACT_APP_PASSWORD_ENCRYPTION_ALGORITHM || 'RSA-OAEP-SHA256',
  prefix: env.REACT_APP_PASSWORD_ENCRYPTION_PREFIX || 'rsa_oaep_b64:',
  public_key_pem: envPublicKey,
}

let importedPublicKeyPromise = null

const isObject = (value) => value !== null && typeof value === 'object'

const isPasswordField = (fieldName = '') => fieldName.toLowerCase().includes('password')

const isAlreadyEncrypted = (value) =>
  typeof value === 'string' && value.startsWith(PASSWORD_ENCRYPTION_CONFIG.prefix)

const pemToArrayBuffer = (pem) => {
  const base64 = pem.replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----|\s+/g, '')
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

const toBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

const getPublicKey = async () => {
  if (!importedPublicKeyPromise) {
    const subtle = window?.crypto?.subtle
    if (!subtle) {
      throw new Error('Web Crypto API is not available')
    }
    if (!PASSWORD_ENCRYPTION_CONFIG.public_key_pem) {
      throw new Error('Missing REACT_APP_PASSWORD_PUBLIC_KEY_PEM in .env')
    }
    importedPublicKeyPromise = subtle.importKey(
      'spki',
      pemToArrayBuffer(PASSWORD_ENCRYPTION_CONFIG.public_key_pem),
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false,
      ['encrypt']
    )
  }
  return importedPublicKeyPromise
}

export const encryptPasswordValue = async (value) => {
  if (!PASSWORD_ENCRYPTION_CONFIG.enabled || typeof value !== 'string' || isAlreadyEncrypted(value)) {
    return value
  }

  const publicKey = await getPublicKey()
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    {name: 'RSA-OAEP'},
    publicKey,
    new TextEncoder().encode(value)
  )

  return `${PASSWORD_ENCRYPTION_CONFIG.prefix}${toBase64(encryptedBuffer)}`
}

const encryptFormData = async (formData) => {
  const nextFormData = new FormData()
  // FormData may contain files; only encrypt password-like text fields.
  for (const [key, value] of formData.entries()) {
    if (isPasswordField(key) && typeof value === 'string') {
      nextFormData.append(key, await encryptPasswordValue(value))
    } else {
      nextFormData.append(key, value)
    }
  }
  return nextFormData
}

const encryptObject = async (value) => {
  if (Array.isArray(value)) {
    const items = await Promise.all(value.map((item) => encryptObject(item)))
    return items
  }

  if (value instanceof FormData) {
    return encryptFormData(value)
  }

  if (!isObject(value)) {
    return value
  }

  const entries = await Promise.all(
    Object.entries(value).map(async ([key, fieldValue]) => {
      if (isPasswordField(key) && typeof fieldValue === 'string') {
        return [key, await encryptPasswordValue(fieldValue)]
      }
      return [key, await encryptObject(fieldValue)]
    })
  )

  return Object.fromEntries(entries)
}

export const encryptPayloadPasswords = async (payload) => {
  if (!PASSWORD_ENCRYPTION_CONFIG.enabled || payload == null) {
    return payload
  }
  return encryptObject(payload)
}

export {PASSWORD_ENCRYPTION_CONFIG}
